import { Navigate, Outlet } from 'react-router';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { Spin } from '@/uicomponents';
import { FLAGS } from '@/config/feature-flags';
import { useFeatureFlagsReady } from '@/services/feature-flags';
import { useAuthStore } from '@/stores/auth-store';

/** Test-only override (set by e2e via addInitScript). Ignored in production builds. */
const testOverride = (): boolean | undefined => {
  if (import.meta.env.PROD) return undefined;
  return (globalThis as unknown as { __FF_DESIGN_SYSTEM__?: boolean }).__FF_DESIGN_SYSTEM__;
};

/** Evaluates the flag — only mounted once the provider has reconciled the user's context. */
const FlagGate = () => {
  const enabled = useBooleanFlagValue(
    FLAGS.designSystemPage.key,
    FLAGS.designSystemPage.defaultValue,
  );
  return enabled ? <Outlet /> : <Navigate to='/' replace />;
};

/**
 * Holds the route on a loader until the authed user has hydrated AND the provider has
 * reconciled that user's context — otherwise the transient `user === null` first render
 * (setUser runs in AppLayout's effect after children mount) would evaluate the anonymous
 * env-level flag and redirect before identity/tenant targeting resolves.
 */
const RealGate = () => {
  const user = useAuthStore((s) => s.user);
  const ready = useFeatureFlagsReady();
  if (user === null || !ready) return <Spin fullscreen />;
  return <FlagGate />;
};

export const DesignSystemGuard = () => {
  const override = testOverride();
  if (override !== undefined) {
    return override ? <Outlet /> : <Navigate to='/' replace />;
  }
  return <RealGate />;
};
